/**
 * Locale Store — Donor Portal
 *
 * Manages the active locale state and language preferences.
 * Uses vue-i18n under the hood (via the i18n plugin helpers) and caches
 * loaded bundles in the i18n instance — no duplicate API calls.
 */
import { defineStore } from 'pinia';
import {
  changeLanguage,
  getCurrentLanguage,
  reloadTranslationsPostAuth,
  clearTenantContext,
  clearBundleCache,
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  type SupportedLanguage,
} from '@/core/plugins/i18n';
import { getApiErrorMessage } from '@/core/services';

interface LocaleState {
  currentLanguage: string;
  isLoading: boolean;
  error: string | null;
  isFullBundleLoaded: boolean;
  availableLanguages: Array<{ code: string; name: string; nativeName: string; direction: string }>;
}

const LANGUAGE_META: Record<string, { name: string; nativeName: string; direction: string }> = {
  en: { name: 'English', nativeName: 'English', direction: 'ltr' },
  de: { name: 'German', nativeName: 'Deutsch', direction: 'ltr' },
  tr: { name: 'Turkish', nativeName: 'Türkçe', direction: 'ltr' },
};

function resolveInitialLanguage(): string {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
    return stored;
  }
  const current = getCurrentLanguage();
  if (current && SUPPORTED_LANGUAGES.includes(current as SupportedLanguage)) {
    return current;
  }
  return DEFAULT_LANGUAGE;
}

export const useLocaleStore = defineStore('locale', {
  state: (): LocaleState => ({
    currentLanguage: resolveInitialLanguage(),
    isLoading: false,
    error: null,
    isFullBundleLoaded: false,
    availableLanguages: SUPPORTED_LANGUAGES.map((code) => ({
      code,
      name: LANGUAGE_META[code]?.name ?? code,
      nativeName: LANGUAGE_META[code]?.nativeName ?? code,
      direction: LANGUAGE_META[code]?.direction ?? 'ltr',
    })),
  }),

  getters: {
    currentLanguageName(): string {
      const entry = this.availableLanguages.find((l) => l.code === this.currentLanguage);
      return entry?.name ?? this.currentLanguage;
    },

    currentLanguageNativeName(): string {
      const entry = this.availableLanguages.find((l) => l.code === this.currentLanguage);
      return entry?.nativeName ?? this.currentLanguage;
    },

    isRtl(): boolean {
      const entry = this.availableLanguages.find((l) => l.code === this.currentLanguage);
      if (entry) return entry.direction === 'rtl';
      const rtl = ['ar', 'he', 'fa', 'ur'];
      return rtl.some((code) => this.currentLanguage.startsWith(code));
    },
  },

  actions: {
    async fetchLanguages(): Promise<void> {
      // Locale API not ready — using static language list defined in LANGUAGE_META.
    },

    async setLanguage(lang: SupportedLanguage): Promise<void> {
      if (this.currentLanguage === lang) return;

      this.isLoading = true;
      this.error = null;

      try {
        await changeLanguage(lang);
        this.currentLanguage = lang;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'apiErrors.failedChangeLanguage');
        console.error('[LocaleStore] Language change failed:', err);
      } finally {
        this.isLoading = false;
      }
    },

    async loadFullBundle(): Promise<void> {
      // Locale API not ready — static English fallback bundle is always fully loaded.
      this.isFullBundleLoaded = true;
    },

    async reloadAfterOverrideChange(): Promise<void> {
      this.isLoading = true;
      try {
        await reloadTranslationsPostAuth();
      } catch (err) {
        console.error('[LocaleStore] Bundle reload after override change failed:', err);
      } finally {
        this.isLoading = false;
      }
    },

    resetOnLogout(): void {
      clearTenantContext();
      clearBundleCache();
      this.isFullBundleLoaded = false;
      this.error = null;
    },
  },

  persist: {
    paths: ['currentLanguage'],
  },
});
